import type { FieldValues, Path } from 'react-hook-form';

// Underlying UI component for multi-line text input.
import type { SDSTextType } from '@repo/sds/SDSText';
import { SDSTextArea } from '@repo/sds/SDSTextArea';

// Shared props type provided by the Controller wrapper (BaseFormField).
import type { ControlledFieldProps } from '../FormField/FormField';

/** Base configuration props specific to the Text Area Input component. */
export interface BaseFormTextAreaInputProps<TFieldValues extends FieldValues> {
  /** Name identifier for the field within the form state. */
  name: Path<TFieldValues>;
  /** Text label displayed for the input. */
  label: string;
  /** Indicates if the field is mandatory. */
  isRequired?: boolean;
  /** Placeholder text displayed when the input is empty. */
  placeholder?: string;
  /** Data attribute for testing purposes. */
  dataTestId?: string;
  /** Maximum length of input value. */
  maxLength?: number;
  /** Typography of the label. */
  labelTextType?: SDSTextType;
}

/** Combined props for the FormTextAreaInput component, including RHF control props. */
interface FormTextAreaInputProps<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
> extends BaseFormTextAreaInputProps<TFieldValues>,
    ControlledFieldProps<TFieldValues, TName> {
  // Includes field, errorMessage, isDisabled
}

/**
 * Renders an SDSTextArea component integrated with React Hook Form,
 * receiving RHF state and handlers via the 'field' prop from a Controller wrapper.
 */
export function FormTextAreaInput<
  TFieldValues extends FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
  field, // RHF field object { value, onChange, onBlur, name, ref }
  errorMessage,
  isDisabled,
  label,
  isRequired = false,
  placeholder,
  dataTestId,
  maxLength,
  labelTextType = 'body-medium',
}: FormTextAreaInputProps<TFieldValues, TName>) {
  // Renders the underlying SDSTextArea, spreading the RHF field object
  // and passing down specific configuration and state props.
  return (
    <SDSTextArea
      {...field}
      label={label}
      errorMessage={errorMessage}
      isRequired={isRequired}
      placeholder={placeholder}
      isDisabled={isDisabled}
      isFullWidth
      dataTestId={dataTestId}
      maxLength={maxLength}
      labelTextType={labelTextType}
    />
  );
}
